// Run bar + output panel rendered under every ```python block in chat.
// State lives in python_sandbox.js's store (keyed by the code), so the
// panel keeps its output when the message re-renders or remounts.
import { useEffect, useState } from "react";
import {
  runAndStore, getRunState, subscribeRuns, takeAutoRun,
  formatRunForChat, resetPythonSession,
} from "./python_sandbox.js";

function useRunState(code) {
  const [, setTick] = useState(0);
  useEffect(() => subscribeRuns(() => setTick((t) => t + 1)), []);
  return getRunState(code);
}

function OutputTable({ table, T }) {
  return (
    <div style={{ overflowX: "auto", maxHeight: 320, overflowY: "auto", border: `1px solid ${T.border}`, borderRadius: 6, margin: "6px 0" }}>
      <table style={{ borderCollapse: "collapse", fontSize: 11, fontFamily: "ui-monospace,Menlo,monospace", width: "auto" }}>
        <thead>
          <tr>
            <th style={{ position: "sticky", top: 0, background: T.headBg, padding: "4px 8px", borderBottom: `1px solid ${T.border}` }}></th>
            {table.columns.map((c, i) => (
              <th key={i} style={{ position: "sticky", top: 0, background: T.headBg, padding: "4px 8px", textAlign: "left", borderBottom: `1px solid ${T.border}`, whiteSpace: "nowrap" }}>{c}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {table.rows.map((r, ri) => (
            <tr key={ri}>
              <td style={{ padding: "3px 8px", color: T.muted, borderBottom: `1px solid ${T.border}` }}>{table.index[ri]}</td>
              {r.map((v, ci) => (
                <td key={ci} style={{ padding: "3px 8px", borderBottom: `1px solid ${T.border}`, whiteSpace: "nowrap" }}>{v === null ? "—" : v}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {table.totalRows > table.rows.length && (
        <div style={{ padding: "4px 8px", fontSize: 10, color: T.muted }}>Showing {table.rows.length} of {table.totalRows.toLocaleString()} rows</div>
      )}
    </div>
  );
}

export default function PythonRunPanel({ code, theme }) {
  const T = {
    text: theme?.text || "#222",
    muted: theme?.textMuted || "#666",
    accent: theme?.accent || "#3b82c4",
    border: theme?.borderSubtle || "rgba(127,127,127,0.2)",
    headBg: theme?.surfaceAlt || "rgba(127,127,127,0.08)",
    danger: theme?.danger || "#e5484d",
  };
  const st = useRunState(code);
  const running = st?.state === "running";
  const res = st?.result || null;

  useEffect(() => {
    if (takeAutoRun(code)) runAndStore(code);
  }, [code]);

  const btn = {
    display: "inline-flex", alignItems: "center", gap: 4, padding: "2px 9px", borderRadius: 5,
    border: `1px solid ${T.border}`, background: "none", color: T.muted, fontSize: 10,
    cursor: "pointer", fontFamily: "monospace",
  };
  const sendToChat = () => {
    try { window.dispatchEvent(new CustomEvent("cf_code_output_to_chat", { detail: { text: formatRunForChat(res) } })); } catch {}
  };
  const saveImage = (b64, i) => {
    const a = document.createElement("a");
    a.href = "data:image/png;base64," + b64;
    a.download = `chart-${i + 1}.png`;
    a.click();
  };

  return (
    <div className="cf-py-run" style={{ borderTop: `1px solid ${T.border}`, padding: "5px 10px 7px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
        <button onClick={() => runAndStore(code)} disabled={running}
          title="Run this Python in a private sandbox in your browser"
          aria-label="Run Python"
          style={{ ...btn, border: `1px solid ${T.accent}66`, color: T.accent, opacity: running ? 0.6 : 1, cursor: running ? "default" : "pointer", fontWeight: 600 }}>
          <svg width="9" height="9" viewBox="0 0 24 24" fill="currentColor"><path d="M6 4l14 8-14 8z" /></svg>
          {running ? (st.status || "Running…") : res ? "Run again" : "Run"}
        </button>
        {res && !running && (
          <button onClick={sendToChat} title="Send this output to the AI so it can interpret it" style={btn}>
            Send output to chat
          </button>
        )}
        {res && !running && (
          <button onClick={() => { if (window.confirm("Reset the Python session? Variables from earlier runs will be cleared.")) resetPythonSession(); }} title="Clear variables from earlier runs" style={btn}>
            Reset session
          </button>
        )}
        {res && !running && (
          <span style={{ marginLeft: "auto", fontSize: 10, color: res.ok ? T.muted : T.danger, fontFamily: "monospace" }}>
            {res.ok ? "✓" : "✕"} {(res.durationMs / 1000).toFixed(1)}s · sandboxed
          </span>
        )}
      </div>
      {res && !running && (
        <div style={{ marginTop: 6 }}>
          {(res.stdout || res.stderr) && (
            <pre style={{ margin: "4px 0", padding: "6px 8px", maxHeight: 260, overflow: "auto", fontSize: 11, lineHeight: 1.45, background: T.headBg, borderRadius: 6, whiteSpace: "pre-wrap", wordBreak: "break-word", color: T.text }}>
              {res.stdout}
              {res.stdout && res.stderr ? "\n" : ""}
              {res.stderr && <span style={{ color: T.muted }}>{res.stderr}</span>}
            </pre>
          )}
          {res.resultRepr && (
            <pre style={{ margin: "4px 0", padding: "6px 8px", maxHeight: 200, overflow: "auto", fontSize: 11, background: T.headBg, borderRadius: 6, whiteSpace: "pre-wrap", color: T.text }}>
              <span style={{ color: T.muted }}>Out: </span>{res.resultRepr}
            </pre>
          )}
          {res.table && <OutputTable table={res.table} T={T} />}
          {res.images.map((b64, i) => (
            <div key={i} style={{ margin: "6px 0", position: "relative" }}>
              <img src={"data:image/png;base64," + b64} alt={`Chart ${i + 1}`} style={{ maxWidth: "100%", height: "auto", borderRadius: 6, background: "#fff", display: "block" }} />
              <button onClick={() => saveImage(b64, i)} style={{ ...btn, position: "absolute", top: 6, right: 6, background: "rgba(255,255,255,0.85)", color: "#333" }}>Save PNG</button>
            </div>
          ))}
          {res.error && (
            <pre style={{ margin: "4px 0", padding: "6px 8px", maxHeight: 220, overflow: "auto", fontSize: 11, background: "rgba(229,72,77,0.08)", color: T.danger, borderRadius: 6, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
              {res.error}
            </pre>
          )}
          {res.ok && !res.stdout && !res.stderr && !res.resultRepr && !res.table && !res.images.length && (
            <div style={{ fontSize: 11, color: T.muted, fontStyle: "italic" }}>Ran with no output.</div>
          )}
        </div>
      )}
    </div>
  );
}
